// OwlWatchList — always-on MODEL view (OWL-M1).
//
// Promoted from fallback-only to default render. Surfaces every fixture
// in the OWL window with the agents' fair-price prediction, honestly
// classified as BET (edge cleared), MODEL (no edge claim — pure
// prediction), or PASS (negative edge, market is right). Founder
// approved full transparency including PASS rows.
//
// Doctrine preserved: fair prediction is shown for every fixture, edge
// labels are honest (no fabricated +%). BET rows here are informational
// only — the lockable BET cards above remain the user's stake path.

import { useOwlWatchList, humanMarket, type OwlWatchItem, type OwlPredictionKind } from "../../owl/useOwlWatchList.ts";

function fmtKickoff(raw: string): string {
  if (!raw) return "";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  const wat = new Date(d.getTime() + 60 * 60 * 1000);
  return `${String(wat.getUTCHours()).padStart(2, "0")}:${String(wat.getUTCMinutes()).padStart(2, "0")}`;
}

// OWL-P1 — muted, premium palette. Avoids the bright violet/gold/green
// the page was leaning on. Slate-cool + parchment + minimal saturation.
const KIND_STYLE: Record<OwlPredictionKind, { label: string; chipBg: string; chipBorder: string; chipText: string; rowOpacity: number }> = {
  BET:   { label: "BET",   chipBg: "rgba(154, 175, 158, 0.10)", chipBorder: "rgba(154, 175, 158, 0.32)", chipText: "#9aaf9e", rowOpacity: 1 },
  MODEL: { label: "MODEL", chipBg: "rgba(180, 188, 200, 0.08)", chipBorder: "rgba(180, 188, 200, 0.24)", chipText: "#b4bcc8", rowOpacity: 1 },
  PASS:  { label: "PASS",  chipBg: "rgba(140, 145, 155, 0.06)", chipBorder: "rgba(140, 145, 155, 0.16)", chipText: "#8c919b", rowOpacity: 0.55 },
};

interface Props {
  sessionDate: string;
}

export function OwlWatchList({ sessionDate }: Props): JSX.Element | null {
  const { items, loading } = useOwlWatchList(sessionDate, 50);

  const counts = {
    BET:   items.filter((i) => i.prediction_kind === "BET").length,
    MODEL: items.filter((i) => i.prediction_kind === "MODEL").length,
    PASS:  items.filter((i) => i.prediction_kind === "PASS").length,
  };

  if (loading) {
    return (
      <div style={{
        background: "var(--owl-elev)",
        border: "1px solid var(--owl-border)",
        borderRadius: 12,
        padding: 24,
        textAlign: "center",
        color: "var(--owl-text-3)",
        fontSize: 12,
        marginTop: 20,
      }}>
        Loading tonight's model view…
      </div>
    );
  }
  if (items.length === 0) return null;

  return (
    <div style={{
      background: "var(--owl-elev)",
      border: "1px solid var(--owl-border)",
      borderRadius: 12,
      overflow: "hidden",
      marginTop: 20,
    }}>
      {/* Header */}
      <div style={{
        padding: "16px 22px",
        borderBottom: "1px solid var(--owl-border)",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 16,
        flexWrap: "wrap",
      }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--owl-text-1)", letterSpacing: "-0.01em" }}>
            Tonight's model view
          </div>
          <div style={{ fontSize: 11, color: "var(--owl-text-3)", marginTop: 4, lineHeight: 1.5, maxWidth: 580 }}>
            The agents' read on every fixture in the window. <b style={{ color: "var(--owl-text-2)" }}>BET</b> = edge cleared a publishable threshold.{" "}
            <b style={{ color: "var(--owl-text-2)" }}>MODEL</b> = our prediction, no edge claim. <b style={{ color: "var(--owl-text-2)" }}>PASS</b> = market is right, we don't disagree.
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, fontFamily: "var(--font-mono)", fontSize: 10 }}>
          <CountChip label="BET" count={counts.BET} kind="BET" />
          <CountChip label="MODEL" count={counts.MODEL} kind="MODEL" />
          <CountChip label="PASS" count={counts.PASS} kind="PASS" />
        </div>
      </div>

      {/* Column header strip */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "60px 1fr 150px 92px 70px",
        gap: 12,
        padding: "8px 22px",
        background: "rgba(255,255,255,0.015)",
        borderBottom: "1px solid var(--owl-border)",
        fontSize: 9,
        textTransform: "uppercase" as const,
        letterSpacing: "0.10em",
        color: "var(--owl-text-3)",
        fontWeight: 600,
      }}>
        <div>Read</div>
        <div>Fixture</div>
        <div>Pick</div>
        <div style={{ textAlign: "right" }}>Price · Edge</div>
        <div style={{ textAlign: "right" }}>Kick · Fair</div>
      </div>

      {/* Rows */}
      {items.map((it, i) => (
        <WatchRow key={it.fixture_id + i} item={it} />
      ))}
    </div>
  );
}

function CountChip({ label: _label, count, kind }: { label: string; count: number; kind: OwlPredictionKind }): JSX.Element {
  const s = KIND_STYLE[kind];
  return (
    <span style={{
      padding: "4px 9px",
      borderRadius: 4,
      background: s.chipBg,
      border: `1px solid ${s.chipBorder}`,
      color: s.chipText,
      fontWeight: 600,
      letterSpacing: "0.06em",
    }}>
      {s.label} · {count}
    </span>
  );
}

function WatchRow({ item }: { item: OwlWatchItem }): JSX.Element {
  const s = KIND_STYLE[item.prediction_kind];
  const edge = item.clv_gap ?? 0;
  const edgePct = edge * 100;
  const fairPct = (item.fair_prob * 100).toFixed(0);
  const offered = item.market_odds ?? item.fair_odds;
  const edgeLabel = edge === 0
    ? "neutral"
    : `${edge > 0 ? "+" : ""}${edgePct.toFixed(1)}%`;
  const edgeColor = item.prediction_kind === "BET"
    ? "#9aaf9e"
    : item.prediction_kind === "PASS"
      ? "#a8a4a0"
      : "var(--owl-text-3)";

  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: "60px 1fr 150px 92px 70px",
      gap: 12,
      padding: "14px 22px",
      borderBottom: "1px solid var(--owl-border)",
      alignItems: "center",
      opacity: s.rowOpacity,
    }}>
      <span style={{
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: "0.10em",
        padding: "3px 8px",
        borderRadius: 3,
        background: s.chipBg,
        border: `1px solid ${s.chipBorder}`,
        color: s.chipText,
        textAlign: "center" as const,
        display: "inline-block",
        width: "fit-content",
      }}>
        {s.label}
      </span>

      <div>
        <div style={{ fontSize: 13, color: "var(--owl-text-1)", fontWeight: 500, letterSpacing: "-0.005em" }}>
          {item.home_team} vs {item.away_team}
        </div>
        <div style={{ fontSize: 10.5, color: "var(--owl-text-3)", marginTop: 2 }}>
          {item.competition}
        </div>
      </div>

      <div>
        <div style={{ fontSize: 12.5, color: "var(--owl-text-1)", fontWeight: 500 }}>
          {item.selection}
        </div>
        <div style={{ fontSize: 10, color: "var(--owl-text-3)", marginTop: 2 }}>
          {humanMarket(item.market)}
        </div>
      </div>

      <div style={{ fontSize: 12, fontFamily: "var(--font-mono)", textAlign: "right" as const }}>
        <div style={{ color: "var(--owl-text-1)", fontWeight: 600 }}>
          {offered ? offered.toFixed(2) : "-"}
        </div>
        <div style={{ fontSize: 10, color: edgeColor, marginTop: 2 }}>
          {edgeLabel}
        </div>
      </div>

      <div style={{ fontSize: 11, color: "var(--owl-text-3)", fontFamily: "var(--font-mono)", textAlign: "right" as const }}>
        <div>{fmtKickoff(item.kickoff_at)}</div>
        <div style={{ fontSize: 10, marginTop: 2 }}>{fairPct}%</div>
      </div>
    </div>
  );
}
